import React, { useMemo, useEffect, useRef, useState } from 'react';
import logo from '../assets/logo.png';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useView } from '../contexts/ViewContext';
import { useAuth } from '../contexts/AuthContext';
import { getUnreadUpsellCount } from '../api/auth';
import DateRangePicker from './DateRangePicker';
import { getDatePickerState } from './header/datePicker';
import ThinScroll from './shared/ThinScroll';
import { orgSelectOptions } from './header/orgOptions';
import { buildGroups, StatusBadge, ChevronDownIcon } from './NavSidebar';
import { canAccessHub, CLIENT_HUB_NAV_VISIBLE } from '../pages/hub/access';

const getRoleName = (role) => {
  switch(Number(role)) {
    case 0: return "Super Admin";
    case 1: return "Admin";
    case 2: return "Manager";
    case 3: return "Client";
    default: return "User";
  }
};

// Custom dropdown that always opens below its trigger. Native <select> hands
// open-direction control to the browser, which sometimes chooses upward — this
// component owns the positioning so both header selectors behave the same.
// Header controls share one fixed height (h-10, matching the date-picker trigger in
// this header) so Org, Property and Date line up — an empty Property label used to
// collapse the button to its padding.
function HeaderSelect({ value, onChange, options, disabled = false, className = '', triggerClassName = '', maxWidth }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const handleKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const selectedLabel = options.find((o) => String(o.value) === String(value))?.label ?? '';

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
        className={`w-full inline-flex items-center justify-between gap-2 text-sm border dark:border-gray-700 rounded-lg px-2.5 h-10 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 font-medium focus:ring-2 focus:ring-[#32B1FF] focus:border-transparent outline-none disabled:opacity-50 disabled:cursor-not-allowed ${triggerClassName}`}
        style={maxWidth ? { maxWidth } : undefined}
      >
        <span className="truncate">{selectedLabel}</span>
        <svg className={`w-3 h-3 text-gray-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <path d="M3 4.5 L6 7.5 L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <ThinScroll className="absolute top-full mt-2 z-50 inline-block rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg max-h-72 overflow-y-auto w-full min-[500px]:w-max min-[500px]:max-w-[90vw] left-0 min-[500px]:left-1/2 min-[500px]:-translate-x-1/2 min-[600px]:left-0 min-[600px]:translate-x-0">
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              disabled={opt.disabled}
              onClick={() => { if (opt.disabled) return; onChange(opt.value); setOpen(false); }}
              className={`block w-full text-left text-sm px-3 py-2 truncate ${
                opt.disabled
                  ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                  : `hover:bg-gray-50 dark:hover:bg-gray-800 ${String(opt.value) === String(value) ? 'bg-[#32B1FF]/10 text-[#32B1FF]' : 'text-gray-900 dark:text-gray-100'}`
              }`}
            >
              {opt.label}
            </button>
          ))}
        </ThinScroll>
      )}
    </div>
  );
}

const AdminHeader = ({
  showNav = true,
  activePage = "Dashboard",
  // 2026-07-27 — some pages scope themselves to a project via the URL
  // (e.g. /client/:projectId, /client-projects/:projectId). On those pages
  // the org+client dropdowns are redundant with the URL, so callers can
  // pass hideClientSelectors={true} to remove both. The date picker still
  // renders (useful for narrowing the transcript window).
  hideClientSelectors = false,
  // 2026-08-25 — optional slot for page-injected content that should appear
  // beside the Property dropdown in the header (e.g. an extra control a page
  // wants surfaced globally). Defaults to null so pages that don't pass it
  // see no change. Rendered in both the ≥1700px inline cluster and the
  // <1700px selectors row, immediately after the Property dropdown. Generic —
  // not tied to any specific page or feature.
  rightSlot = null,
}) => {
  const view = useView();
  const {
    orgs, loadingOrgs, selectedOrgId, selectOrg, orgClients,
    clientProjects,
    selectedProjectId, selectProject,
    rangeStart, rangeEnd, datePreset, setDateRange,
    role: activeRole, isClient, isAdmin, isStaff,
    canSelectClients,
    canSwitchProject,
    canOpenClientDashboard,
  } = view;
  // 2026-09-14 — Dashboard ↔ Client Hub toggle. Super admin / admin / owner
  // (isAdmin) get a "Client Hub" switch in the header. Agency-internal staff
  // (isStaff) never see it — they're Client-Hub-only and are bounced from
  // /dashboard by the route guard. This mirrors the hub-side toggle
  // (HubTopBar.canToggleToDashboard = isAdmin && !isStaff) and the documented
  // routing rule in src/lib/roles.js, so the two surfaces read as one segmented
  // control. The earlier gate also required useHubAccess() (workspaces.length >
  // 0); that hid the toggle on first load when fn_my_workspaces hadn't resolved
  // or returned empty for an admin, so the menu never appeared. The DB remains
  // the wall — every hub function checks auth.uid() itself — so showing the
  // link is safe even for an admin who holds no workspace row.
  // 2026-09-28 (Epic 2, item 35 Q2) — additive gate: the six full-access staff
  // (canOpenClientDashboard) ALSO get the toggle, without becoming isAdmin.
  // Mirrors HubTopBar's gate so the two surfaces stay a segmented control.
  const canToggleToHub = (!!isAdmin && !isStaff) || canOpenClientDashboard;
  const [userInfo, setUserInfo] = useState({
    username: (localStorage.getItem('username') && localStorage.getItem('username') !== 'undefined') ? localStorage.getItem('username') : "username",
    role: localStorage.getItem('role') || 0,
    avatar: localStorage.getItem('avatar') || null,
  });

  // Publish the header's measured height as a CSS variable so sticky/fixed
  // children (NavSidebar, LiveFeed) can offset themselves correctly when the
  // header wraps to multiple rows on narrow viewports.
  const headerRef = useRef(null);
  useEffect(() => {
    if (!headerRef.current) return;
    const ro = new ResizeObserver(([entry]) => {
      document.documentElement.style.setProperty(
        '--app-header-h',
        `${Math.round(entry.contentRect.height)}px`,
      );
    });
    ro.observe(headerRef.current);
    return () => ro.disconnect();
  }, []);

  const initials = useMemo(() => {
    return userInfo.username
      ? userInfo.username
          .split(' ')
          .map((n) => n[0]?.toUpperCase() || '')
          .join('')
          .slice(0, 2)
      : '';
  }, [userInfo.username]);

  const [showLogout, setShowLogout] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState({});
  const navigate = useNavigate();
  const location = useLocation();
  // Global date-range picker state for this route — one resolver call instead
  // of composing hidesGlobalDateRange + disablesGlobalDateRange + a hand-
  // written hint string. The hint text lives in DISABLE_REASONS (in
  // ./header/datePicker) so both headers share one source of truth and can't
  // drift apart the way they did when each inlined its own copy.
  const { state: datePickerState, disabledReason: datePickerHint } = getDatePickerState(location.pathname);
  const hideDatePicker = datePickerState === 'hidden';
  const disableDatePicker = datePickerState === 'disabled';
  const { isDarkMode, toggleTheme } = useTheme();
  const { signOut } = useAuth();

  // Multi-Org Membership Epic 1 — role is per-organization. The header's
  // org-scoped gates (admin nav, notifications, role label) follow the active
  // org, read from ViewContext (activeRole/isClient/isAdmin). userInfo
  // now only carries username + avatar (not org-scoped).
  const role = activeRole;

  // 2026-07-28 — when admin/super-admin switches org or property from the
  // header, jump back to /dashboard. Sub-pages like /bookings, /google-ads,
  // /visitors are all scoped to the current client, so a switch mid-page
  // would either show stale data or load a different client's data under an
  // unrelated header. Bouncing to /dashboard makes the switch obvious and
  // avoids that whole class of confusion. No-op if already on /dashboard.
  const handleOrgChange = (v) => {
    selectOrg(v);
    if (location.pathname !== '/dashboard') navigate('/dashboard');
  };
  const handleProjectChange = (v) => {
    selectProject(v || '');
    if (location.pathname !== '/dashboard') navigate('/dashboard');
  };
  // D1 (2026-08-26) — scoped users (role === 3) may switch among their granted
  // properties. Their list is `clientProjects` (= allProjects = the granted set
  // from fn_get_my_clients); admins (canSelectClients) keep using `orgClients`
  // (projects in the selected org). Computed once and reused by both the
  // ≥1700px inline cluster and the <1700px selectors row below.
  const projectSelectOptions = activeRole === 3
    ? (clientProjects.length === 0
        ? [{ value: '', label: 'No properties available' }]
        : [
            { value: '', label: '— select property —' },
            ...clientProjects.map((p) => ({ value: p.id, label: p.name })),
          ])
    : (!selectedOrgId
        ? [{ value: '', label: 'Pick an organization first' }]
        : orgClients.length === 0
          ? [{ value: '', label: 'No properties in this org' }]
          : [
              { value: '', label: '— select property —' },
              ...orgClients.map((p) => ({ value: p.id, label: p.name })),
            ]);
  const mobileGroups = useMemo(() => buildGroups({
    showChat: !!view.showChat,
    showVoice: !!view.showVoice,
    showBookings: !!view.showBookings,
    showCdp: !!view.showCdp,
    showCompetitive: !!view.showCompetitiveIntelligence,
    showGoogleAds: !!view.showGoogleAds,
    showAnalytics: !!view.showGoogleAnalytics,
    showAim: !!view.showAim,
    showEvents: !!view.showEventIntelligence,
    showRoute: !!view.showRouteIntelligence,
    showWeatherIntelligence: !!view.showWeatherIntelligence,
    showLocalIntelligence: !!view.showLocalIntelligence,
    showSearchConsole: !!view.showSearchConsole,
    showPortal: !!view.showPortal,
    // ClientHub — hidden from nav for all users for now (CLIENT_HUB_NAV_VISIBLE).
    // Super Admin reaches it via the Profile page; the route gate (canAccessHub)
    // is unchanged so direct URL access still works for entitled roles.
    showHubClients: CLIENT_HUB_NAV_VISIBLE && canAccessHub(view),
    isClient,
    isDarkMode,
  }), [view, isClient, isDarkMode]);

  // Auto-expand the group containing the active route when the menu opens
  useEffect(() => {
    if (!mobileMenuOpen) return;
    const activeGroup = mobileGroups.find(g => g.items.some(i => i.route && location.pathname === i.route));
    if (activeGroup) {
      setMobileExpanded(prev => prev[activeGroup.key] ? prev : { ...prev, [activeGroup.key]: true });
    }
  }, [mobileMenuOpen, mobileGroups, location.pathname]);

  const toggleMobileGroup = (key) => setMobileExpanded(prev => ({ ...prev, [key]: !prev[key] }));
  const handleMobileItemClick = (item) => {
    if (item.status === 'now' && item.enabled && item.route) {
      navigate(item.route);
      setMobileMenuOpen(false);
    }
  };

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Fetch unread notification count (only for admin/super admin in the active org)
  useEffect(() => {
    if (isAdmin) {
      const fetchUnreadCount = async () => {
        try {
          const response = await getUnreadUpsellCount();
          setUnreadCount(response.data?.count || 0);
        } catch (err) {
          console.error('Failed to fetch unread count:', err);
        }
      };
      fetchUnreadCount();
      // Poll every 30 seconds — skip while the tab is backgrounded.
      const interval = setInterval(() => { if (!document.hidden) fetchUnreadCount(); }, 30000);
      return () => clearInterval(interval);
    }
  }, [isAdmin]);

  const handleLogout = async () => {
    setShowLogout(false);
    await signOut();
    navigate('/');
  };

  useEffect(() => {
    const onStorageChange = () => {
      setUserInfo({
        username: (localStorage.getItem('username') && localStorage.getItem('username') !== 'undefined') ? localStorage.getItem('username') : "username",
        role: localStorage.getItem('role') || 0,
        avatar: localStorage.getItem('avatar') || null,
      });
    };
    window.addEventListener('storage', onStorageChange);
    return () => window.removeEventListener('storage', onStorageChange);
  }, []);

  return (
    <>
      <header ref={headerRef} className="sticky top-0 z-40 bg-white dark:bg-gray-900 shadow-sm dark:shadow-[0_4px_10px_-2px_rgba(0,0,0,0.6)] transition-colors duration-200">
        <div className="flex justify-between items-center gap-2 sm:gap-3 px-3 sm:px-4 md:px-6 lg:px-10 py-2.5 sm:py-3 md:py-4 lg:py-5">
          <div className="flex items-center gap-2 md:gap-4 lg:gap-8 min-w-0">
            {/* 2026-07-31 — cap logo width on tiny viewports so it can't push
                the right cluster off-screen. h-7 at <sm (28px) keeps the whole
                top row under ~44px on iPhone SE. */}
            <img src={logo} alt="Insider Perks" className="h-7 sm:h-8 md:h-10 w-auto max-w-[110px] sm:max-w-none shrink-0" />
            {showNav && (
              <nav className="hidden min-[850px]:flex items-center gap-1">
                <Link
                  to="/dashboard"
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activePage === "Dashboard"
                      ? "bg-[#32B1FF] text-white"
                      : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
                  }`}
                >
                  Dashboard
                </Link>
                {/* Dashboard ↔ Client Hub toggle (2026-09-14). Super admin / admin
                    / owner who hold a workspace can switch into the Client Hub ops
                    surface from the analytics dashboard. Staff roles never see
                    this — they're Client-Hub-only. Active side reads as a pill so
                    the two read as a segmented toggle. */}
                {canToggleToHub && (
                  <Link
                    to="/hub/now"
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      location.pathname.startsWith('/hub')
                        ? 'bg-[#32B1FF] text-white'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                    }`}
                    title="Switch to Client Hub"
                  >
                    Client Hub
                  </Link>
                )}
                {/* Organizations, Clients, Users moved out of the top nav into the
                    ClientHub menu system (the in-hub HubSidebar "Admin" section
                    and the NavSidebar "Client Hub" group). Still reachable by
                    direct URL (/organizations, /clients, /users) and via the
                    /hub/admin/* aliases. See App.js §Admin routes. */}
              </nav>
            )}
          </div>

          {/* Center slot — pages portal scroll-promoted filters here (e.g. Google Ads campaign filters).
              Gated to min-[1900px]: the right cluster (org + client + date + icons + user) is ~940px
              and the nav ~540px, so the campaign filters (~410px) only fit on the top row past ~1900px.
              Below that the slot stays hidden and the page keeps its own full-width in-flow
              CampaignFilterBar (never crams the filters into a starved flex-1 box). */}
          <div id="app-header-center-slot" className="hidden min-[1900px]:flex items-center justify-center flex-1 min-w-0 px-6" />

          <div className="flex items-center gap-0.5 sm:gap-1.5 md:gap-3 relative">
            {/* Global: Client → Date (≥1700px only, otherwise on second row).
                2026-07-31 — raised from ≥1500px to ≥1700px because 13"/14"
                laptops at 1440-1680px CSS were landing in the overlap zone
                where the top-bar cluster (logo + nav + dropdowns + icons +
                user) needed more room than the viewport offered.
                Second "Project" picker removed 2026-07-07 — legacy Rigsby-era
                artifact where one client could own multiple chatbot deployments.
                In the Supabase model client = project (1:1), so the sub-picker
                was always empty. ViewContext.selectedProjectId + selectProject
                stay defined for backward compat with any page still reading them. */}
            {canSwitchProject && (
              <div className="hidden min-[1700px]:flex items-center gap-2">
                {!hideClientSelectors && (
                  <>
                    {/* Dropdown 1: Organization — picks an org (CRR Hospitality,
                        Team Outsider, etc.). Platform_admin sees every org; scoped
                        roles see only their memberships (fn_get_my_organizations).
                        D1 (2026-08-26): hidden for role-3 users — they don't pick
                        orgs, only their granted properties. */}
                    {canSelectClients && (
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Org</span>
                        <HeaderSelect
                          value={selectedOrgId}
                          onChange={handleOrgChange}
                          maxWidth={220}
                          options={
                            loadingOrgs
                              ? [{ value: '', label: 'Loading organizations…' }]
                              : orgs.length === 0
                              ? [{ value: '', label: 'No organizations available' }]
                              : orgSelectOptions(orgs)
                          }
                        />
                      </div>
                    )}
                    {/* Dropdown 2: Property — admins pick within the selected org;
                        scoped users (role 3) pick among their granted properties
                        (clientProjects). Feature-flag menu gates read from
                        `selectedProject`. D1 (2026-08-26). */}
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Property</span>
                      <HeaderSelect
                        value={selectedProjectId || ''}
                        onChange={handleProjectChange}
                        maxWidth={220}
                        options={projectSelectOptions}
                      />
                    </div>
                    {rightSlot}
                  </>
                )}
                {!hideDatePicker && (
                  <DateRangePicker
                    triggerClassName="h-10"
                    startDate={rangeStart}
                    endDate={rangeEnd}
                    activePreset={datePreset}
                    onApply={(start, end, preset) => setDateRange(start, end, preset)}
                    disabled={disableDatePicker}
                    hint={datePickerHint}
                  />
                )}
              </div>
            )}

            <button
              onClick={toggleTheme}
              className="p-1.5 sm:p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              title={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
              aria-label={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
            >
              {isDarkMode ? (
                <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="w-5 h-5 text-gray-700" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                </svg>
              )}
            </button>

            {/* Notification Bell (Admin/Super Admin only) — hidden on the
                tightest mobile widths (<sm) to reserve room for burger +
                avatar; still reachable inside the mobile menu below. */}
            {isAdmin && (
              <Link
                to="/notifications"
                className="relative p-1.5 sm:p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors hidden min-[400px]:inline-flex"
                title="Upsell Notifications"
                aria-label="Upsell Notifications"
              >
                <svg className="w-5 h-5 text-gray-700 dark:text-gray-300" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center shadow-lg">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </Link>
            )}

            <Link
              to="/profile"
              className="flex items-center gap-2 md:gap-3 px-0.5 sm:px-1 md:px-3 py-1.5 md:py-2 md:-mx-3 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer"
              title="View Profile"
              aria-label="View Profile"
            >
              <div className={`h-8 w-8 sm:h-9 sm:w-9 md:h-10 md:w-10 rounded-full flex items-center justify-center text-white text-sm sm:text-base md:text-lg font-bold overflow-hidden shrink-0 ${
                activePage === "Profile"
                  ? "ring-2 ring-[#32B1FF] ring-offset-2 dark:ring-offset-gray-900"
                  : ""
              } ${userInfo.avatar ? "" : "bg-gray-400 dark:bg-gray-600"}`}>
                {userInfo.avatar ? (
                  <img src={userInfo.avatar} alt="" className="w-full h-full object-cover" />
                ) : (
                  initials
                )}
              </div>
              <div className="hidden xl:flex flex-col min-w-0">
                <span className="text-gray-800 dark:text-gray-200 font-medium text-sm truncate">{userInfo.username}</span>
                <span className="text-gray-400 dark:text-gray-500 text-xs">{getRoleName(role)}</span>
              </div>
            </Link>
            <button
              className="hidden sm:inline-block px-3 py-1 bg-red-100 dark:bg-red-900/30 text-red-500 dark:text-red-400 font-semibold rounded hover:bg-red-200 dark:hover:bg-red-900/50 transition text-sm"
              onClick={() => setShowLogout(true)}
              title="Logout"
            >
              Logout
            </button>
            <button
              className="sm:hidden p-1.5 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-500 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50 transition"
              onClick={() => setShowLogout(true)}
              title="Logout"
              aria-label="Logout"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </button>
            {showNav && (
              <button
                className="min-[850px]:hidden p-1.5 sm:p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                onClick={() => setMobileMenuOpen((v) => !v)}
                title="Menu"
                aria-label="Toggle navigation menu"
              >
                <svg className="w-5 h-5 text-gray-700 dark:text-gray-300" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  {mobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Selectors row (visible below 1700px) — placed BEFORE mobile nav so filters stay close to content when burger is collapsed.
            The whole row is skipped when both the org+client dropdowns AND
            the date picker would be hidden (nothing to render).
            2026-07-31 — bumped inline breakpoint 1500→1700 (13"/14" laptops
            at 1440-1680px CSS width were overlapping the top-row cluster).
            Added flex-wrap so Org/Property/Date wrap onto a second line at
            intermediate widths (~600-900px) instead of being crushed to
            unreadable widths in a single row. Each item takes ~50% width on
            wrap so Org+Property sit side-by-side and the DatePicker flows
            to a third row when it can't fit next to them. */}
        {canSwitchProject && !(hideClientSelectors && hideDatePicker) && (
          <div className="min-[1700px]:hidden border-t border-gray-100 dark:border-gray-800 px-3 sm:px-4 md:pl-[50px] md:pr-6 lg:pr-10 py-2 flex flex-col min-[500px]:flex-row min-[500px]:flex-wrap min-[500px]:items-center gap-2">
            {!hideClientSelectors && (
              <>
                {/* D1 (2026-08-26): org dropdown hidden for role-3 users — they
                    don't pick orgs, only their granted properties. */}
                {canSelectClients && (
                  <div className="w-full min-[500px]:basis-[calc(50%-0.25rem)] min-[900px]:basis-0 min-[900px]:flex-1 min-w-0 flex items-center gap-1.5">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 shrink-0">Org</span>
                    <HeaderSelect
                      value={selectedOrgId}
                      onChange={handleOrgChange}
                      className="flex-1 min-w-0"
                      options={
                        loadingOrgs
                          ? [{ value: '', label: 'Loading organizations…' }]
                          : orgs.length === 0
                          ? [{ value: '', label: 'No organizations available' }]
                          : orgSelectOptions(orgs)
                      }
                    />
                  </div>
                )}
                <div className="w-full min-[500px]:basis-[calc(50%-0.25rem)] min-[900px]:basis-0 min-[900px]:flex-1 min-w-0 flex items-center gap-1.5">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 shrink-0">Property</span>
                  <HeaderSelect
                    value={selectedProjectId || ''}
                    onChange={handleProjectChange}
                    className="flex-1 min-w-0"
                    options={projectSelectOptions}
                  />
                </div>
                {rightSlot}
              </>
            )}
            {!hideDatePicker && (
              <DateRangePicker
                className="w-full min-[500px]:basis-full min-[900px]:basis-0 min-[900px]:flex-1 min-[500px]:min-w-0"
                triggerClassName="w-full justify-between h-10"
                startDate={rangeStart}
                endDate={rangeEnd}
                activePreset={datePreset}
                onApply={(start, end, preset) => setDateRange(start, end, preset)}
                disabled={disableDatePicker}
                hint={datePickerHint}
              />
            )}
          </div>
        )}

        {/* Mobile nav menu (below 850px) — placed AFTER selectors */}
        {showNav && mobileMenuOpen && (
          <nav className="min-[850px]:hidden border-t border-gray-100 dark:border-gray-800 px-2 py-2 flex flex-col gap-0.5 max-h-[75vh] overflow-y-auto">
            {/* Organizations, Clients, Users moved into the ClientHub menu system
                (see the desktop comment above). The mobile menu renders the same
                feature groups — including the Client Hub group when visible —
                via mobileGroups below. */}

            {/* Dashboard ↔ Client Hub toggle (mobile). Same gate as desktop:
                super admin / admin / owner who hold a workspace. Staff roles
                are Client-Hub-only and never see the dashboard side. */}
            {canToggleToHub && (
              <div className="flex gap-2 pb-2 mb-1 border-b border-gray-100 dark:border-gray-800">
                <Link
                  to="/dashboard"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors text-center ${
                    activePage === 'Dashboard'
                      ? 'bg-[#32B1FF] text-white'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  Dashboard
                </Link>
                <Link
                  to="/hub/now"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors text-center ${
                    location.pathname.startsWith('/hub')
                      ? 'bg-[#32B1FF] text-white'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  Client Hub
                </Link>
              </div>
            )}

            {/* Feature groups — same source of truth as NavSidebar */}
            {mobileGroups.map((group) => {
              const isExpanded = !!mobileExpanded[group.key];
              const groupHasActive = group.items.some(i => i.route && location.pathname === i.route);
              return (
                <div key={group.key}>
                  <button
                    type="button"
                    onClick={() => toggleMobileGroup(group.key)}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg transition-colors text-left ${
                      groupHasActive
                        ? "text-[#32B1FF] font-semibold"
                        : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                    }`}
                  >
                    <span className={groupHasActive ? "text-[#32B1FF]" : "text-gray-500 dark:text-gray-400"}>
                      {group.icon}
                    </span>
                    <span className="text-xs font-semibold uppercase tracking-wide flex-1">{group.label}</span>
                    <ChevronDownIcon className={`text-gray-400 transition-transform ${isExpanded ? "" : "-rotate-90"}`} />
                  </button>
                  {isExpanded && (
                    <div className="mt-0.5 mb-1 space-y-0.5">
                      {group.items.map((item) => {
                        const isActive = item.route && location.pathname === item.route;
                        const isSoonOrLater = item.status === "soon" || item.status === "later";
                        const isDisabled = !isSoonOrLater && !item.enabled && !isActive;
                        const isClickable = item.status === "now" && (item.enabled || isActive) && !isActive;
                        return (
                          <button
                            key={item.key}
                            type="button"
                            disabled={!isClickable && !isActive}
                            onClick={() => isClickable && handleMobileItemClick(item)}
                            title={
                              item.status === "soon" ? `${item.label} — Coming Soon` :
                              item.status === "later" ? `${item.label} — Planned` :
                              isDisabled ? `${item.label} — Not enabled for this project` :
                              item.label
                            }
                            className={`w-full flex items-center gap-2 pl-9 pr-3 py-2 rounded-md text-sm transition-colors ${
                              isActive
                                ? "bg-[#32B1FF]/10 text-[#32B1FF] font-semibold"
                                : isSoonOrLater
                                ? "text-gray-500 dark:text-gray-500 cursor-default"
                                : isDisabled
                                ? "text-gray-400 dark:text-gray-600 cursor-not-allowed opacity-70"
                                : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                            }`}
                          >
                            <span className={`shrink-0 ${
                              isActive ? "text-[#32B1FF]" :
                              isSoonOrLater ? "text-gray-400 dark:text-gray-600" :
                              isDisabled ? "text-gray-300 dark:text-gray-700" :
                              "text-gray-500 dark:text-gray-400"
                            }`}>
                              {item.icon}
                            </span>
                            <span className="flex-1 text-left truncate">{item.label}</span>
                            <StatusBadge status={item.status} />
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        )}

      </header>

      {showLogout && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-20 dark:bg-opacity-60">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-8 shadow-xl w-full max-w-sm">
            <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-3">Logout Confirmation</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6">Are you sure you want to logout?</p>
            <div className="flex justify-end gap-2">
              <button
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 rounded hover:bg-gray-300 dark:hover:bg-gray-600 font-semibold text-gray-800 dark:text-gray-200"
                onClick={() => setShowLogout(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-red-500 dark:bg-red-700 text-white rounded hover:bg-red-600 dark:hover:bg-red-800 font-semibold"
                onClick={handleLogout}
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminHeader;
